/* Deploying is one project's work, so it has one project's address.
 *
 * It used to be a conditional half of the supervision layout — the same file opened the
 * escalation stream, published two different navs, and switched workspace id on a pathname
 * test. Releases belong to a project and supervision belongs to the floor, so they are two
 * layouts under two addresses now, sharing only the one thing they genuinely share: the
 * fleet door's seat, on one query key.
 *
 * Live supervision (`stations`) sits here too: watching a deployed robot's episodes is part
 * of deploying it, and what it is watched for (its ladder) is its deployment's supervision,
 * revised on the robot's own page. The people who answer a robot's call for help work in
 * Teleoperators.
 */

import { SeatOrgKind } from "@sx/protocol/fleet/supervision";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Outlet, createFileRoute } from "@tanstack/react-router";
import { retainedQueryData } from "@sx/api-client";

import {
  registerWorkspaceHeader,
  type WorkspaceNavItem,
} from "../../../lib/nav";
import { usePrincipalId } from "../../../lib/browser-state";
import { started } from "../../../lib/prefetch";
import { loadedSession } from "../../../lib/session";
import { WorkspaceGate } from "../../../shell/WorkspaceGate";
import { useProjectAddress } from "../../../shell/project";
import { SeatProvider, fleetSeatQuery } from "../../../workspaces/fleet/seat";

export const Route = createFileRoute("/_console/p/$org/$project/deploy")({
  loader: async ({ context }) => {
    const { fleetSeatQuery: loaderFleetSeatQuery } = await import(
      "../../../workspaces/fleet/seat"
    );
    const principalId = loadedSession(context.queryClient)?.principal.id;
    if (principalId === undefined) return;
    await started(
      context.queryClient.ensureQueryData(loaderFleetSeatQuery(principalId)),
    );
  },
  component: DeployLayout,
});

function DeployLayout() {
  const seatQuery = useQuery(fleetSeatQuery(usePrincipalId()));
  // Through `retainedQueryData`, never `.data`: a seat restored from the last reload must
  // not outlive its own revocation.
  const seat = retainedQueryData(seatQuery);
  const address = useProjectAddress();

  // A teleop vendor works other people's robots through the help queue: it is shown the
  // robots, never the live supervision of a fleet that is not its own.
  const vendor = seat?.orgKind === SeatOrgKind.TELEOP_VENDOR;
  useEffect(
    () =>
      registerWorkspaceHeader({
        workspace: "fleet-deployment",
        // The robots, each opening its own page (its trail reads Deployments / Robots / its
        // name), and how they are supervised live.
        items: [
          { to: "/p/$org/$project/deploy", params: address, label: "Robots", icon: "robots", end: true },
          ...(vendor
            ? []
            : ([
                { to: "/p/$org/$project/deploy/stations", params: address, label: "Monitor", icon: "gauge" },
              ] satisfies WorkspaceNavItem[])),
        ],
      }),
    [address, vendor],
  );

  if (seat === undefined)
    return <WorkspaceGate workspace="fleet-deployment" query={seatQuery} />;

  return (
    <SeatProvider seat={seat}>
      <Outlet />
    </SeatProvider>
  );
}
